import { useIntl } from '@umijs/max';
import React, { useEffect, useRef, useState } from 'react';
import {
  buildTextIndex,
  createOverlay,
  findInText,
  fuzzyFindInFullText,
  normalise,
  waitForContainerWidth,
} from '../SearchPdfViewerWithHighlight/pdfHighlight.utils';

export { buildTextIndex, findInText, fuzzyFindInFullText, normalise };

interface PdfViewerWithHighlightProps {
  blobUrl: string;
  page: number;
  highlight?: string;
  height?: string;
}

const PdfViewerWithHighlight: React.FC<PdfViewerWithHighlightProps> = ({
  blobUrl,
  page,
  highlight,
  height = '400px',
}) => {
  const intl = useIntl();
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!blobUrl) return;

    let cancelled = false;
    let loadingTask: any = null;
    let renderTask: any = null;
    let textLayer: any = null;
    let pdf: any = null;
    let pdfDestroyed = false;
    let canvas: HTMLCanvasElement | null = null;
    setError(null);
    const destroyPdf = async () => {
      if (pdf && !pdfDestroyed) {
        pdfDestroyed = true;
        await pdf.destroy?.();
      }
    };

    const render = async () => {
      try {
        const pdfjs = await import('pdfjs-dist');
        const { TextLayerBuilder } = await import('pdfjs-dist/web/pdf_viewer.mjs');
        if (cancelled) return;
        pdfjs.GlobalWorkerOptions.workerSrc = new URL(
          'pdfjs-dist/build/pdf.worker.min.mjs',
          import.meta.url,
        ).toString();

        const documentOptions = {
          url: blobUrl,
          isEvalSupported: false,
          enableScripting: false,
        } as Parameters<typeof pdfjs.getDocument>[0] & { enableScripting: boolean };
        loadingTask = pdfjs.getDocument(documentOptions);
        pdf = await loadingTask.promise;
        if (cancelled) return;

        const targetPage = Math.min(Math.max(1, page), pdf.numPages);

        const pdfPage = await pdf.getPage(targetPage);
        if (cancelled) return;

        const container = containerRef.current;
        if (!container) return;

        container.innerHTML = '';

        // If the popover hasn't laid out yet clientWidth is 0 — wait up to ~300ms
        const containerWidth = await waitForContainerWidth(containerRef);
        if (cancelled) return;

        const dpr = window.devicePixelRatio || 1;
        const defaultViewport = pdfPage.getViewport({ scale: 1 });
        const scale = containerWidth / defaultViewport.width;
        const viewport = pdfPage.getViewport({ scale });

        // Canvas
        canvas = document.createElement('canvas');
        canvas.width = viewport.width * dpr;
        canvas.height = viewport.height * dpr;
        canvas.style.display = 'block';
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;

        const ctx = canvas.getContext('2d')!;
        ctx.scale(dpr, dpr);
        renderTask = pdfPage.render({ canvasContext: ctx, viewport });
        await renderTask.promise;
        renderTask = null;
        if (cancelled) return;

        // Text layer — needed to find bounding rects of matching text
        textLayer = new TextLayerBuilder({ pdfPage });
        const textLayerDiv = textLayer.div;
        textLayerDiv.className = 'pdf-text-layer';
        textLayerDiv.style.cssText = `
          position: absolute; top: 0; left: 0;
          width: ${viewport.width}px; height: ${viewport.height}px;
          overflow: hidden; opacity: 0; pointer-events: none; user-select: none;
        `;
        textLayerDiv.style.setProperty('--scale-factor', String(scale));

        const wrapper = document.createElement('div');
        wrapper.style.cssText = `position: relative; width: ${viewport.width}px; height: ${viewport.height}px;`;
        wrapper.appendChild(canvas);
        wrapper.appendChild(textLayerDiv);
        container.appendChild(wrapper);

        // Render text layer spans
        await textLayer.render(viewport);
        if (cancelled) return;

        if (!highlight) return;

        // Wait one rAF so the browser computes layout/transforms on the text divs
        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => resolve());
        });
        if (cancelled) return;

        const { fullText, divMap } = buildTextIndex(textLayerDiv);

        // Highlight keyword occurrences (first match only + fuzzy fallback)
        let firstHighlightTop: number | null = null;
        if (highlight) {
          const needle = normalise(highlight);
          if (needle) {
            const match = findInText(fullText, needle);

            if (match) {
              const matchingDivs = divMap
                .filter(({ start, end }) => end > match.matchIdx && start < match.matchEnd)
                .map(({ div }) => div);

              for (const div of matchingDivs) {
                const { top } = createOverlay(
                  wrapper,
                  div,
                  'pdf-highlight-overlay',
                  'rgba(255, 229, 0, 0.25)',
                  viewport.width,
                );
                if (firstHighlightTop === null) firstHighlightTop = top;
              }
            }
          }
        }

        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => resolve());
        });
        if (cancelled) return;

        // Scroll to first keyword highlight
        if (firstHighlightTop !== null) {
          const scrollTop = firstHighlightTop - container.clientHeight / 2;
          container.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
        }
      } catch (err) {
        if (!cancelled) {
          console.error('PDF render error:', err);
          setError(
            intl.formatMessage({
              id: 'components.pdfViewerWithHighlight.renderError',
              defaultMessage: 'Failed to render PDF',
            }),
          );
        }
      } finally {
        await destroyPdf();
      }
    };

    render();
    return () => {
      cancelled = true;
      renderTask?.cancel?.();
      textLayer?.cancel?.();
      if (canvas) canvas.width = canvas.height = 0;
      if (pdf) void destroyPdf();
      else loadingTask?.destroy?.();
    };
  }, [blobUrl, page, highlight]);

  if (error) {
    return <div style={{ padding: 16, color: 'red' }}>{error}</div>;
  }

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height, overflow: 'auto', background: '#f5f5f5' }}
    />
  );
};

export default PdfViewerWithHighlight;
